import { useState, useEffect, useCallback } from "react";
import {
  isPushSupported,
  isFirebaseConfigured,
  getNotificationPermission,
  requestPushPermission,
  onForegroundMessage,
} from "@/lib/firebase/pushNotifications";
import { useToast } from "@/hooks/use-toast";

const STORAGE_KEY = "push_notifications_asked";

interface UsePushNotificationsReturn {
  /** Whether push notifications are supported in this browser */
  isSupported: boolean;
  /** Whether Firebase is properly configured */
  isConfigured: boolean;
  /** Current notification permission status */
  permission: NotificationPermission;
  /** Whether the user has already been asked about notifications */
  hasBeenAsked: boolean;
  /** Whether the permission request is in progress */
  isLoading: boolean;
  /** Enable push notifications */
  enableNotifications: () => Promise<boolean>;
  /** Mark that the user has been asked (used for "not now" option) */
  markAsAsked: () => void;
  /** Whether to show the notification prompt */
  shouldShowPrompt: boolean;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [isSupported] = useState(() => isPushSupported());
  const [isConfigured] = useState(() => isFirebaseConfigured());
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    getNotificationPermission()
  );
  const [hasBeenAsked, setHasBeenAsked] = useState(() =>
    localStorage.getItem(STORAGE_KEY) === "true"
  );
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Set up foreground message listener
  useEffect(() => {
    if (!isSupported || !isConfigured || permission !== "granted") {
      return;
    }

    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast for foreground notifications
      toast({
        title: payload.title || "Nueva notificación",
        description: payload.body,
      });
    });

    return unsubscribe;
  }, [isSupported, isConfigured, permission, toast]);

  const markAsAsked = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    setHasBeenAsked(true);
  }, []);

  const enableNotifications = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const result = await requestPushPermission();

      // Update permission state
      setPermission(getNotificationPermission());

      // Mark as asked regardless of result
      markAsAsked();

      if (result.success) {
        toast({
          title: "Notificaciones activadas",
          description: "Te avisaremos cuando tengas una nueva ruta asignada",
        });
        return true;
      } else {
        if (result.error === "Notification permission denied") {
          toast({
            title: "Permiso denegado",
            description:
              "Podés habilitarlas desde la configuración de tu navegador",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: result.error || "No se pudieron activar las notificaciones",
            variant: "destructive",
          });
        }
        return false;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Ocurrió un error al activar las notificaciones",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [markAsAsked, toast]);

  // Determine if we should show the prompt
  const shouldShowPrompt =
    isSupported &&
    isConfigured &&
    !hasBeenAsked &&
    permission === "default";

  return {
    isSupported,
    isConfigured,
    permission,
    hasBeenAsked,
    isLoading,
    enableNotifications,
    markAsAsked,
    shouldShowPrompt,
  };
}

